import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, Wrench, Truck, Info } from "lucide-react";
import AudioRecorder from "@/components/AudioRecorder";
import DiagnosisCard from "@/components/DiagnosisCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { submitDiagnosis } from "@/lib/api";
import { saveHistory } from "@/lib/history";

const QUICK_TAGS = [
  "Squeal on braking",
  "Engine knock at idle",
  "Battery clicking",
  "Burning smell",
  "Whine on acceleration",
  "Grinding on turns",
];

const WARNING_LIGHTS = ["Check Engine", "Oil", "Battery", "ABS", "Temperature", "Airbag"];

const WHEN_OPTIONS = [
  "At idle",
  "While accelerating",
  "While braking",
  "While turning",
  "Over bumps",
  "Cold start",
  "After warm-up",
  "Constant",
];

export default function DiagnosePage() {
  const nav = useNavigate();
  const [description, setDescription] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [whenOccurs, setWhenOccurs] = useState("");
  const [warningLights, setWarningLights] = useState([]);
  const [audioBlob, setAudioBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleLight = (light) => {
    setWarningLights((prev) =>
      prev.includes(light) ? prev.filter((l) => l !== light) : [...prev, light]
    );
  };

  const addTag = (tag) => {
    setDescription((prev) => (prev ? `${prev}. ${tag}` : tag));
  };

  const runDiagnosis = async () => {
    if (!description.trim() && !audioBlob) {
      toast.error("Describe the issue or record engine audio to continue.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await submitDiagnosis({
        description,
        vehicle: { year, make, model },
        whenOccurs,
        warningLights,
        audioBlob,
      });
      setResult(data);
      saveHistory({
        ...data,
        vehicle: [year, make, model].filter(Boolean).join(" "),
      });
      toast.success("Diagnosis complete");
      setTimeout(() => {
        document.getElementById("diagnosis-result")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Diagnosis failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="grid lg:grid-cols-[1fr_auto] gap-8 items-start" data-testid="hero-section">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">
              Claude Sonnet 5 · Whisper AI
            </span>
          </div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-slate-50">
            Diagnose Your Ride
            <br />
            <span className="text-amber-500">Before It Diagnoses You.</span>
          </h1>
          <p className="mt-5 text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">
            Record the sound, describe what's happening, and get a probable diagnosis in under 30 seconds — with likely parts, cost estimates, and a direct route to the nearest verified mechanic or 24/7 tow.
          </p>
        </div>
        <div className="hidden lg:block">
          <img
            src="https://images.unsplash.com/photo-1777991506119-aec61a4a3c94?crop=entropy&cs=srgb&fm=jpg&w=400&q=80"
            alt="dashboard"
            className="w-72 rounded-2xl border border-slate-800 shadow-2xl"
          />
        </div>
      </section>

      {/* Form */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <AudioRecorder onAudioReady={setAudioBlob} />

          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">
                Quick Symptoms
              </div>
              <div className="font-display text-lg font-bold mb-3">Tap common issues</div>
              <div className="flex flex-wrap gap-2">
                {QUICK_TAGS.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => addTag(tag)}
                    data-testid={`quick-tag-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-800 hover:bg-amber-500 hover:text-slate-950 text-slate-300 transition-colors duration-150 border border-slate-700"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">
              Description
            </div>
            <div className="font-display text-lg font-bold mb-3">What's happening?</div>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="issue-description-input"
              placeholder="e.g. High-pitched squeal when I start the car in the morning, stops after about 30 seconds..."
              className="min-h-[120px] bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wide">Year</Label>
              <Input
                value={year}
                onChange={(e) => setYear(e.target.value)}
                data-testid="vehicle-year-input"
                placeholder="2019"
                className="bg-slate-950/70 border-slate-700 text-slate-100"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wide">Make</Label>
              <Input
                value={make}
                onChange={(e) => setMake(e.target.value)}
                data-testid="vehicle-make-input"
                placeholder="Honda"
                className="bg-slate-950/70 border-slate-700 text-slate-100"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wide">Model</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                data-testid="vehicle-model-input"
                placeholder="Civic"
                className="bg-slate-950/70 border-slate-700 text-slate-100"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">
              When does it occur?
            </Label>
            <Select value={whenOccurs} onValueChange={setWhenOccurs}>
              <SelectTrigger
                data-testid="when-occurs-select"
                className="bg-slate-950/70 border-slate-700 text-slate-100"
              >
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {WHEN_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`when-option-${o.replace(/\s+/g, "-").toLowerCase()}`}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">
              Warning lights on
            </Label>
            <div className="flex flex-wrap gap-2">
              {WARNING_LIGHTS.map((light) => (
                <button
                  key={light}
                  onClick={() => toggleLight(light)}
                  data-testid={`warning-${light.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${
                    warningLights.includes(light)
                      ? "bg-red-500/20 border-red-500 text-red-300"
                      : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {light}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={runDiagnosis}
            disabled={loading}
            data-testid="diagnose-submit-btn"
            className={`w-full h-14 rounded-full font-display font-bold uppercase tracking-wider text-base bg-amber-500 hover:bg-amber-600 text-slate-950 shadow-[0_0_25px_rgba(245,158,11,0.4)] relative overflow-hidden ${
              loading ? "scanning-overlay" : ""
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 animate-spin" />
                Analyzing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Run AI Diagnosis
              </span>
            )}
          </Button>
        </div>
      </section>

      {/* Result */}
      {result && (
        <section id="diagnosis-result" data-testid="diagnosis-result-section">
          <DiagnosisCard
            result={result}
            onFindMechanics={() => nav("/mechanics")}
            onCallTow={() => nav("/tow-trucks")}
          />
        </section>
      )}

      {/* Info footer */}
      {!result && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex items-start gap-3">
          <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-400 leading-relaxed">
            AutoPulse is an <span className="text-slate-200 font-semibold">assistive diagnostic tool</span>, not a replacement for hands-on inspection. Confidence scores reflect the AI's reasoning — always verify with a qualified technician before major repairs.
          </p>
        </section>
      )}
    </div>
  );
}
